import React, { useEffect, useState } from 'react';
import { ShieldCheck, Plus, Trash2, Download, Upload, RefreshCw, Copy, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

const today = () => new Date().toISOString().slice(0, 10);
const slug = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || ('id-' + Math.random().toString(36).slice(2, 7));

function download(filename, text) {
  const blob = new Blob([text], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

const th = { padding: '10px 12px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', textTransform: 'uppercase' };
const td = { padding: '11px 12px', fontSize: 13, color: 'var(--ink)' };
const mono = { fontFamily: 'monospace', fontSize: 11 };

function CopyBtn({ text }) {
  const [done, setDone] = useState(false);
  return (
    <button className="btn btn-secondary btn-sm" title="Copiar" onClick={async () => {
      try { await navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); } catch { /* noop */ }
    }}>
      {done ? <Check size={12} /> : <Copy size={12} />}
    </button>
  );
}

export default function Aprobadores() {
  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const [roster, setRoster] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeded, setSeeded] = useState(false);

  const [builder, setBuilder] = useState({ tenant: '', approvers: [] });
  const [signedText, setSignedText] = useState('');
  const [uploadMsg, setUploadMsg] = useState(null);
  const [uploading, setUploading] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [rc, en] = await Promise.all([
        fetch(`${API_URL}/roster/current`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/roster/enrollments?status=pending`, { headers }).then(r => r.json()),
      ]);
      setRoster(rc.roster || null);
      setEnrollments(Array.isArray(en.enrollments) ? en.enrollments : []);
    } catch { /* noop */ }
    finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  // Sembrar el builder con los aprobadores vigentes (una vez, al cargar).
  useEffect(() => {
    if (seeded || loading) return;
    seedFromCurrent();
    setSeeded(true);
    // eslint-disable-next-line
  }, [loading]);

  const seedFromCurrent = () => {
    setBuilder({
      tenant: roster?.tenant || '',
      approvers: (roster?.approvers || []).map(a => ({
        id: a.id || slug(a.name), name: a.name || '', pubkey: a.pubkey || '',
        added_at: a.added_at || today(), revoked: !!a.revoked,
      })),
    });
  };

  const nextVersion = (roster?.version || 0) + 1;

  const setAp = (i, patch) => setBuilder(b => ({ ...b, approvers: b.approvers.map((a, j) => j === i ? { ...a, ...patch } : a) }));
  const addAp = (seed = {}) => setBuilder(b => ({ ...b, approvers: [...b.approvers, { id: '', name: '', pubkey: '', added_at: today(), revoked: false, ...seed }] }));
  const removeAp = (i) => setBuilder(b => ({ ...b, approvers: b.approvers.filter((_, j) => j !== i) }));

  const buildJson = () => {
    const approvers = builder.approvers
      .filter(a => (a.pubkey || '').trim())
      .map(a => ({ id: a.id || slug(a.name), name: (a.name || '').trim(), pubkey: a.pubkey.trim(), added_at: a.added_at || today(), revoked: !!a.revoked }));
    return JSON.stringify({ tenant: (builder.tenant || '').trim() || 'default', version: nextVersion, issued_at: new Date().toISOString(), approvers }, null, 2);
  };

  const handleDownload = () => {
    const withKey = builder.approvers.filter(a => (a.pubkey || '').trim()).length;
    if (withKey === 0) { alert('Agregá al menos un aprobador con su clave pública.'); return; }
    download(`roster-v${nextVersion}.json`, buildJson());
  };

  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setSignedText(String(reader.result || ''));
    reader.readAsText(f);
  };

  const handleUpload = async () => {
    let body;
    try { body = JSON.parse(signedText); } catch { setUploadMsg({ ok: false, text: 'El contenido no es un JSON válido.' }); return; }
    setUploading(true); setUploadMsg(null);
    try {
      const r = await fetch(`${API_URL}/roster`, { method: 'POST', headers, body: JSON.stringify(body) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      const sig = d.signature_verified === true ? 'verificada' : d.signature_verified === false ? 'NO verificada' : 'sin raíz configurada';
      setUploadMsg({ ok: true, text: `Roster v${d.version} subido — ${d.approvers} aprobador(es), firma ${sig}.` });
      setSignedText('');
      loadAll();
    } catch (e) { setUploadMsg({ ok: false, text: e.message }); }
    finally { setUploading(false); }
  };

  const decide = async (id, status) => {
    const r = await fetch(`${API_URL}/roster/enrollments/${id}`, { method: 'PATCH', headers, body: JSON.stringify({ status }) });
    const d = await r.json().catch(() => ({}));
    if (r.ok) loadAll(); else alert(d.error || 'Error');
  };

  const approveAndAdd = (en) => {
    setBuilder(b => b.approvers.some(a => a.pubkey === en.pubkey)
      ? b
      : { ...b, approvers: [...b.approvers, { id: slug(en.name || en.user_email), name: en.name || en.user_email || '', pubkey: en.pubkey, added_at: today(), revoked: false }] });
    decide(en.id, 'approved');
  };

  const verifiedBadge = (v) => v === true
    ? <span className="badge badge-active" style={{ color: 'var(--green)' }}>firma verificada</span>
    : v === false
      ? <span className="badge badge-critical" style={{ color: 'var(--red)' }}>firma NO válida</span>
      : <span className="badge badge-warning" style={{ color: 'var(--amber)' }}>sin raíz configurada</span>;

  return (
    <div style={{ maxWidth: 920, margin: '0 auto', padding: '24px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ background: '#E6F1FB', borderRadius: 10, padding: 8 }}>
            <ShieldCheck size={20} color="var(--accent)" />
          </div>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--ink)' }}>Aprobadores</div>
            <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>Roster firmado de quién puede aprobar comandos en los equipos (ADR 0012)</div>
          </div>
        </div>
        <button className="btn btn-secondary" onClick={loadAll} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw size={14} /> Actualizar
        </button>
      </div>

      {/* ── Roster vigente ─────────────────────────────────────────── */}
      <div className="card" style={{ padding: '18px 20px', marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: 'var(--ink)' }}>Roster vigente</div>
        {loading ? (
          <div style={{ padding: 20, textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
        ) : !roster ? (
          <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>
            No hay ningún roster cargado. Los aprobadores vigentes son solo las <strong>claves compiladas</strong> en el agente.
            Subí un roster firmado abajo para gestionarlos sin recompilar la flota.
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14, fontSize: 13, color: 'var(--ink-2)' }}>
              <span><strong style={{ color: 'var(--ink)' }}>v{roster.version}</strong></span>
              {verifiedBadge(roster.signature_verified)}
              <span>emitido {roster.issued_at ? new Date(roster.issued_at).toLocaleDateString() : '—'}</span>
              <span>subido por {roster.uploaded_by || '—'} · {roster.uploaded_at ? new Date(roster.uploaded_at).toLocaleString() : '—'}</span>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
                  <th style={th}>Aprobador</th><th style={th}>Huella</th><th style={th}>Estado</th><th style={th}>Alta</th>
                </tr></thead>
                <tbody>
                  {roster.approvers.map((a, i) => (
                    <tr key={a.id || i} style={{ borderBottom: '1px solid var(--bg)' }}>
                      <td style={{ ...td, fontWeight: 600 }}>{a.name || a.id}</td>
                      <td style={{ ...td, ...mono, color: 'var(--ink-2)' }}>{a.fingerprint || '—'}</td>
                      <td style={td}>{a.revoked
                        ? <span className="badge badge-critical" style={{ color: 'var(--red)' }}>revocado</span>
                        : <span className="badge badge-active" style={{ color: 'var(--green)' }}>activo</span>}</td>
                      <td style={{ ...td, color: 'var(--ink-2)' }}>{a.added_at || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* ── Armar roster nuevo ─────────────────────────────────────── */}
      <div className="card" style={{ padding: '18px 20px', marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>Armar roster nuevo <span style={{ fontWeight: 400, color: 'var(--ink-3)' }}>→ será v{nextVersion}</span></div>
          <button className="btn btn-secondary btn-sm" onClick={seedFromCurrent}>Cargar los vigentes</button>
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 14 }}>
          Editá la lista, descargá el <code>roster.json</code>, firmalo offline con <code>roster-cli sign</code> (clave raíz) y subí el <code>.signed.json</code> en el paso siguiente. La versión sube sola.
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Tenant (identificador, informativo)</label>
          <input className="form-input" style={{ maxWidth: 280 }} value={builder.tenant} onChange={e => setBuilder(b => ({ ...b, tenant: e.target.value }))} placeholder="ej: itil_cdm" />
        </div>

        {builder.approvers.map((a, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '180px 1fr auto auto', gap: 8, alignItems: 'center', marginBottom: 8 }}>
            <input className="form-input" style={{ fontSize: 13 }} value={a.name} onChange={e => setAp(i, { name: e.target.value })} placeholder="Nombre" />
            <input className="form-input" style={{ ...mono }} value={a.pubkey} onChange={e => setAp(i, { pubkey: e.target.value })} placeholder="Clave pública (MFkw…) — de la pantalla Aprobaciones de la app" />
            <label style={{ fontSize: 12, color: 'var(--ink-2)', display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={a.revoked} onChange={e => setAp(i, { revoked: e.target.checked })} /> revocado
            </label>
            <button className="btn btn-danger btn-sm" onClick={() => removeAp(i)} title="Quitar"><Trash2 size={12} /></button>
          </div>
        ))}
        {builder.approvers.length === 0 && <div style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 8 }}>Sin aprobadores. Agregá uno o cargá los vigentes.</div>}

        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => addAp()}><Plus size={13} /> Agregar aprobador</button>
          <button className="btn btn-primary btn-sm" onClick={handleDownload} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Download size={13} /> Descargar roster sin firmar (v{nextVersion})
          </button>
        </div>
      </div>

      {/* ── Subir roster firmado ───────────────────────────────────── */}
      <div className="card" style={{ padding: '18px 20px', marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6, color: 'var(--ink)' }}>Subir roster firmado</div>
        <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 12 }}>
          Pegá el contenido del <code>.signed.json</code> o cargá el archivo. El servidor valida la versión (debe ser mayor a la vigente) y la firma contra la raíz.
        </div>
        <input type="file" accept=".json,application/json" onChange={onFile} style={{ fontSize: 13, marginBottom: 10, display: 'block' }} />
        <textarea className="form-input" style={{ ...mono, width: '100%', minHeight: 120, resize: 'vertical' }} value={signedText} onChange={e => setSignedText(e.target.value)} placeholder='{ "tenant": "...", "version": N, "issued_at": "...", "approvers": [...], "signature": "..." }' />
        {uploadMsg && (
          <div style={{ fontSize: 13, marginTop: 10, color: uploadMsg.ok ? 'var(--green)' : 'var(--red)' }}>
            {uploadMsg.ok ? '✓ ' : '✗ '}{uploadMsg.text}
          </div>
        )}
        <div style={{ marginTop: 12 }}>
          <button className="btn btn-primary" onClick={handleUpload} disabled={uploading || !signedText.trim()} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Upload size={14} /> {uploading ? 'Subiendo…' : 'Subir roster firmado'}
          </button>
        </div>
      </div>

      {/* ── Solicitudes de enrolamiento ────────────────────────────── */}
      <div className="card" style={{ padding: '18px 20px' }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6, color: 'var(--ink)' }}>Solicitudes de enrolamiento <span style={{ fontWeight: 400, color: 'var(--ink-3)' }}>({enrollments.length} pendiente{enrollments.length === 1 ? '' : 's'})</span></div>
        <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 12 }}>
          Teléfonos que pidieron ser aprobadores. "Aprobar" los suma al roster de arriba (después descargá, firmá y subí para activarlos).
        </div>
        {enrollments.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>No hay solicitudes pendientes.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
                <th style={th}>Nombre</th><th style={th}>Usuario</th><th style={th}>Huella</th><th style={th}>Fecha</th><th style={{ ...th, textAlign: 'right' }}>Acciones</th>
              </tr></thead>
              <tbody>
                {enrollments.map(en => (
                  <tr key={en.id} style={{ borderBottom: '1px solid var(--bg)' }}>
                    <td style={{ ...td, fontWeight: 600 }}>{en.name || '—'}</td>
                    <td style={{ ...td, color: 'var(--ink-2)' }}>{en.user_email || '—'}</td>
                    <td style={{ ...td, ...mono, color: 'var(--ink-2)' }}>
                      {en.fingerprint || '—'} {en.pubkey && <CopyBtn text={en.pubkey} />}
                    </td>
                    <td style={{ ...td, color: 'var(--ink-2)' }}>{en.requested_at ? new Date(en.requested_at).toLocaleDateString() : '—'}</td>
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button className="btn btn-primary btn-sm" onClick={() => approveAndAdd(en)} style={{ marginRight: 6 }}>Aprobar</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => decide(en.id, 'rejected')}>Rechazar</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
